import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { reportService } from '../../services/reportService';
import { userService } from '../../services/api';
import DateRangeFilter from './DateRangeFilter';
import { Download, AlertTriangle } from 'lucide-react';
import SortableHeader, { useSort } from './SortableHeader';
import RevenueDrillDownPanel from './RevenueDrillDownPanel';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const RevenueReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [attorneyId, setAttorneyId] = useState('');
  const [attorneys, setAttorneys] = useState([]);

  useEffect(() => {
    userService.getUsers().then(res => {
      const active = (res.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
      setAttorneys(active);
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      setError(null);
      const params = { start_date: startDate, end_date: endDate };
      if (attorneyId) params.attorney_id = attorneyId;
      const response = await reportService.getRevenue(params);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching revenue report:', err);
      setError('Failed to load Revenue report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, attorneyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('client_name', 'asc');

  const [drillContext, setDrillContext] = useState(null);
  const openDrillDown = (item, column) => {
    if (!item.case_id) return;
    const isCollected = column.startsWith('collected');
    setDrillContext({
      case_id: item.case_id,
      case_number: item.case_number,
      case_title: item.case_title,
      client_name: item.client_name,
      type: isCollected ? 'collected' : 'billed',
      column,
      start_date: startDate,
      end_date: endDate,
      attorney_id: attorneyId || null,
    });
  };

  const handleDateChange = ({ start_date, end_date }) => {
    setStartDate(start_date);
    setEndDate(end_date);
  };

  const handleExportCSV = () => {
    const params = { start_date: startDate, end_date: endDate };
    if (attorneyId) params.attorney_id = attorneyId;
    reportService.downloadCSV(reportService.getRevenue, params, 'revenue_report.csv');
  };

  const filters = (
    <div className="flex flex-wrap items-center gap-4">
      <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />
      <select
        value={attorneyId}
        onChange={(e) => setAttorneyId(e.target.value)}
        className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
      >
        <option value="">All Attorneys</option>
        {attorneys.map(a => (
          <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
        ))}
      </select>
    </div>
  );

  if (!startDate || !endDate) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-gray-900">Revenue Report</h2>
        {filters}
        <div className="text-center py-12 text-gray-500">
          Select a date range to generate the revenue report.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  // Group items by client for subtotals
  const clientGroups = {};
  for (const item of sortedItems) {
    if (!clientGroups[item.client_name]) clientGroups[item.client_name] = [];
    clientGroups[item.client_name].push(item);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">
          Revenue Report
          {summary.attorney_name && <span className="text-gray-500 font-normal ml-2">— {summary.attorney_name}</span>}
        </h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {filters}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Billed Time</p>
          <p className="text-lg font-bold text-blue-700">{formatCurrency(summary.total_billed_time || 0)}</p>
          <p className="text-xs text-gray-400">{parseFloat(summary.total_billed_hours || 0).toFixed(1)} hrs</p>
        </div>
        <div className="bg-indigo-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Billed Expenses</p>
          <p className="text-lg font-bold text-indigo-700">{formatCurrency(summary.total_billed_expense || 0)}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Collected Time</p>
          <p className="text-lg font-bold text-green-700">{formatCurrency(summary.total_collected_time || 0)}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Collected Expenses</p>
          <p className="text-lg font-bold text-emerald-700">{formatCurrency(summary.total_collected_expense || 0)}</p>
        </div>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No revenue data for the selected period.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader sticky label="Case" sortKey="case_number" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader sticky label="Unbilled Time" sortKey="unbilled_time" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader sticky label="Unbilled Hrs" sortKey="unbilled_hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader sticky label="Unbilled Exp" sortKey="unbilled_expense" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader sticky label="Billed Time" sortKey="billed_time" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader sticky label="Billed Hrs" sortKey="billed_hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader sticky label="Billed Exp" sortKey="billed_expense" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader sticky label="Collected Time" sortKey="collected_time" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader sticky label="Collected Exp" sortKey="collected_expense" sortConfig={sortConfig} onSort={requestSort} align="right" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(clientGroups).map(([clientName, cases]) => (
                  <React.Fragment key={clientName}>
                    {/* Client header */}
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-4 py-2">
                        {cases[0]?.client_id ? (
                          <Link to={`/clients/${cases[0].client_id}`} className="font-semibold text-gray-900 hover:text-yellow-700 hover:underline">
                            {clientName}
                          </Link>
                        ) : (
                          <span className="font-semibold text-gray-900">{clientName}</span>
                        )}
                        {cases[0]?.case_type && (
                          <span className="ml-2 text-xs text-gray-500">({cases[0].case_type})</span>
                        )}
                      </td>
                    </tr>
                    {/* Case rows */}
                    {cases.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 pl-8 text-sm text-gray-900">
                          {item.case_id ? (
                            <Link to={`/cases/${item.case_id}`} className="text-yellow-700 hover:text-yellow-900 hover:underline">
                              {item.case_number}
                            </Link>
                          ) : (
                            item.case_number
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(item.unbilled_time)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-600">{parseFloat(item.unbilled_hours).toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(item.unbilled_expense)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900 hover:bg-yellow-50 cursor-pointer" onClick={() => openDrillDown(item, 'billed_time')}>{formatCurrency(item.billed_time)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900 hover:bg-yellow-50 cursor-pointer" onClick={() => openDrillDown(item, 'billed_hours')}>{parseFloat(item.billed_hours).toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900 hover:bg-yellow-50 cursor-pointer" onClick={() => openDrillDown(item, 'billed_expense')}>{formatCurrency(item.billed_expense)}</td>
                        <td className="px-4 py-2 text-sm text-right text-green-700 font-medium hover:bg-green-50 cursor-pointer" onClick={() => openDrillDown(item, 'collected_time')}>{formatCurrency(item.collected_time)}</td>
                        <td className="px-4 py-2 text-sm text-right text-green-700 font-medium hover:bg-green-50 cursor-pointer" onClick={() => openDrillDown(item, 'collected_expense')}>{formatCurrency(item.collected_expense)}</td>
                      </tr>
                    ))}
                    {/* Client subtotal */}
                    {cases.length > 1 && (
                      <tr className="bg-gray-50 font-medium text-sm">
                        <td className="px-4 py-1 pl-8 text-gray-700">Client Total</td>
                        <td className="px-4 py-1 text-right">{formatCurrency(cases.reduce((s, c) => s + parseFloat(c.unbilled_time), 0))}</td>
                        <td className="px-4 py-1 text-right">{cases.reduce((s, c) => s + parseFloat(c.unbilled_hours), 0).toFixed(2)}</td>
                        <td className="px-4 py-1 text-right">{formatCurrency(cases.reduce((s, c) => s + parseFloat(c.unbilled_expense), 0))}</td>
                        <td className="px-4 py-1 text-right">{formatCurrency(cases.reduce((s, c) => s + parseFloat(c.billed_time), 0))}</td>
                        <td className="px-4 py-1 text-right">{cases.reduce((s, c) => s + parseFloat(c.billed_hours), 0).toFixed(2)}</td>
                        <td className="px-4 py-1 text-right">{formatCurrency(cases.reduce((s, c) => s + parseFloat(c.billed_expense), 0))}</td>
                        <td className="px-4 py-1 text-right text-green-700">{formatCurrency(cases.reduce((s, c) => s + parseFloat(c.collected_time), 0))}</td>
                        <td className="px-4 py-1 text-right text-green-700">{formatCurrency(cases.reduce((s, c) => s + parseFloat(c.collected_expense), 0))}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {/* Grand total */}
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td className="px-4 py-3 text-sm">Total</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(summary.total_unbilled_time || 0)}</td>
                  <td className="px-4 py-3 text-sm text-right">{parseFloat(summary.total_unbilled_hours || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(summary.total_unbilled_expense || 0)}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(summary.total_billed_time || 0)}</td>
                  <td className="px-4 py-3 text-sm text-right">{parseFloat(summary.total_billed_hours || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(summary.total_billed_expense || 0)}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-700">{formatCurrency(summary.total_collected_time || 0)}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-700">{formatCurrency(summary.total_collected_expense || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RevenueDrillDownPanel
        isOpen={!!drillContext}
        onClose={() => setDrillContext(null)}
        context={drillContext}
      />
    </div>
  );
};

export default RevenueReport;
